import type { RaceSettings } from '@cube-rush/shared';
import { useRef } from 'preact/hooks';
import { ru } from '../../i18n/ru';
import { raceSummary, raceSummaryParts, raceSummaryText } from '../race/raceSummary';
import './RaceCard.css';

export interface RaceCardProps {
  settings: RaceSettings;
  /** The host between races: a tap changes the race (otherwise it only shows it). */
  editable: boolean;
  onOpen(): void;
}

/**
 * «Гонка» in the room: what the next race (or the running one) is, for everybody. A button: the
 * host opens the race dialog to change it, everybody else to see what it means. It flashes once
 * when the race changes.
 */
export function RaceCard({ settings, editable, onOpen }: RaceCardProps) {
  // Every change after the first look starts the flash again (a fresh element each time).
  const key = raceSummary(settings);
  const shown = useRef(key);
  const flashes = useRef(0);
  if (shown.current !== key) {
    shown.current = key;
    flashes.current++;
  }
  const label = `${editable ? ru.race.edit : ru.race.view}: ${raceSummaryText(settings)}`;
  return (
    <button
      type="button"
      class={`race-card${editable ? ' race-card--edit' : ''}`}
      aria-label={label}
      onClick={onOpen}
    >
      {flashes.current > 0 && (
        <span key={flashes.current} class="race-card__flash" aria-hidden="true" />
      )}
      <span class="race-card__label">{ru.race.title}</span>
      <Summary parts={raceSummaryParts(settings)} variant="long" />
      {/* Phones held sideways: one short line («🏁 10 · 📈 Нарастающая»). */}
      <Summary parts={raceSummaryParts(settings, true)} variant="short" />
      {editable && (
        <span class="race-card__edit" aria-hidden="true">
          ✏️
        </span>
      )}
    </button>
  );
}

/** The summary may wrap between its halves, never inside one («🏁 10 уровней» stays whole). */
function Summary({ parts, variant }: { parts: [string, string]; variant: 'long' | 'short' }) {
  return (
    <span class={`race-card__summary race-card__summary--${variant}`}>
      <span class="race-card__part">{parts[0]}</span> ·{' '}
      <span class="race-card__part">{parts[1]}</span>
    </span>
  );
}
